import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Search, Link2 } from 'lucide-react';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { productsApi } from '../../../api/products.js';
import { ApiError } from '../../../api/client.js';
import { t } from '../../../lib/i18n.js';

import { AttributesEditor } from './AttributesEditor.jsx';

/**
 * Add a variant to a base product, two ways:
 *  - "Collega esistente": search the catalogue by SKU or title and link an
 *    existing standalone product as a variant (sets its parentId).
 *  - "Crea nuovo": create a brand new variant with its own SKU, price and
 *    attributes; shared content is inherited from the base unless copied.
 */
export function VariantCreateDialog({ open, onClose, base, onCreated }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState('link'); // 'link' | 'create'

  // Create-mode form state.
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [priceAmount, setPriceAmount] = useState('');
  const [attrs, setAttrs] = useState({ peso: '', gusto: '' });
  const [copyIngredients, setCopyIngredients] = useState(true);

  // Link-mode search state.
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  const reset = () => {
    setMode('link');
    setSku(''); setName(''); setPriceAmount(''); setAttrs({ peso: '', gusto: '' }); setCopyIngredients(true);
    setTerm(''); setDebounced('');
  };
  // Reset on close so the next open starts fresh (done in the handler, not in an
  // effect, to avoid cascading renders).
  const handleClose = () => { reset(); onClose(); };

  // Debounce the search term so we do not query on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(id);
  }, [term]);

  // The text index covers title/brand but NOT the SKU, so search both the free
  // text (`q`) and the dedicated SKU filter, then merge unique results.
  const searchQuery = useQuery({
    queryKey: ['products', 'variant-search', debounced],
    enabled: open && mode === 'link' && debounced.length >= 2,
    queryFn: async () => {
      const [byText, bySku] = await Promise.all([
        productsApi.list({ q: debounced, limit: 8 }),
        productsApi.list({ sku: debounced, limit: 8 })
      ]);
      const seen = new Set();
      const items = [];
      for (const p of [...(byText.items || []), ...(bySku.items || [])]) {
        if (seen.has(String(p._id))) continue;
        seen.add(String(p._id));
        items.push(p);
      }
      return { items };
    }
  });

  // Candidates: exclude the base itself and products already attached to a base
  // (a product can be a variant of only one base).
  const candidates = useMemo(() => {
    const items = (searchQuery.data && searchQuery.data.items) || [];
    return items.filter((p) => String(p._id) !== String(base?._id) && !p.parentId);
  }, [searchQuery.data, base]);

  const attach = useMutation({
    mutationFn: (productId) => productsApi.attachVariant(base._id, { productId }),
    onSuccess: () => {
      toast.success('Variante collegata.');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      onCreated && onCreated();
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const create = useMutation({
    mutationFn: () => {
      const variantAttributes = {};
      Object.entries(attrs).forEach(([k, v]) => { if (k.trim() && String(v).trim()) variantAttributes[k.trim()] = String(v).trim(); });
      const copyFields = copyIngredients ? ['ingredients', 'ingredientLines'] : [];
      return productsApi.createVariant(base._id, {
        sku: sku.toUpperCase(),
        name: name.trim() || undefined,
        price: { amount: Number(priceAmount), currency: 'EUR', taxIncluded: true },
        variantAttributes,
        copyFields,
        status: 'active'
      });
    },
    onSuccess: () => {
      toast.success('Variante creata.');
      queryClient.invalidateQueries({ queryKey: ['products'] });
      onCreated && onCreated();
      onClose();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const pending = attach.isPending || create.isPending;
  const createValid = /^[A-Z0-9_-]{2,64}$/i.test(sku) && Number(priceAmount) >= 0 && priceAmount !== '';

  return (
    <Modal
      open={open}
      onClose={pending ? undefined : handleClose}
      title={`Nuova variante di ${base?.name || ''}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={pending}>{t('common.cancel')}</Button>
          {mode === 'create' && (
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!createValid}>Crea variante</Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Mode switch */}
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant={mode === 'link' ? 'primary' : 'secondary'} onClick={() => setMode('link')}>Collega esistente</Button>
          <Button type="button" variant={mode === 'create' ? 'primary' : 'secondary'} onClick={() => setMode('create')}>Crea nuovo</Button>
        </div>

        {mode === 'link' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-muted">Cerca un prodotto già a catalogo per SKU o titolo e collegalo come variante.</p>
            <Input
              label="Cerca prodotto"
              placeholder="SKU o titolo"
              value={term}
              autoFocus
              onChange={(e) => setTerm(e.target.value)}
            />
            <div className="max-h-72 overflow-y-auto rounded-md border border-border">
              {debounced.length < 2 ? (
                <p className="flex items-center gap-2 p-3 text-sm text-ink-faint"><Search className="h-4 w-4" /> Digita almeno 2 caratteri.</p>
              ) : searchQuery.isLoading ? (
                <p className="p-3 text-sm text-ink-muted">Ricerca in corso...</p>
              ) : candidates.length === 0 ? (
                <p className="p-3 text-sm text-ink-muted">Nessun prodotto collegabile trovato. Passa a &quot;Crea nuovo&quot;.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {candidates.map((p) => (
                    <li key={p._id} className="flex items-center justify-between gap-3 p-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">{p.name}</p>
                        <p className="truncate text-xs text-ink-muted">{p.sku}{p.brand ? ` · ${p.brand}` : ''}</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        loading={attach.isPending && attach.variables === p._id}
                        disabled={pending}
                        onClick={() => attach.mutate(p._id)}
                      >
                        <Link2 className="mr-1 h-3.5 w-3.5" /> Collega
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-ink-muted">La variante eredita descrizione, tassonomia e letture ORME dal prodotto base. Imposta solo ciò che cambia.</p>
            <Input label="SKU" hint="maiuscolo, cifre, - o _ (2-64)" value={sku} onChange={(e) => setSku(e.target.value)} />
            <Input label="Nome (opzionale)" placeholder={base?.name} value={name} onChange={(e) => setName(e.target.value)} />
            <AttributesEditor value={attrs} onChange={setAttrs} label="Attributi (es. Peso, Gusto)" />
            <Input type="number" min={0} step="0.01" label="Prezzo (EUR)" value={priceAmount} onChange={(e) => setPriceAmount(e.target.value)} />
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={copyIngredients} onChange={(e) => setCopyIngredients(e.target.checked)} />
              Copia gli ingredienti dal base (consigliato se cambia solo il peso)
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
